import React, { useEffect } from "react";
import PropTypes from "prop-types";

import { withStyles } from "@material-ui/styles";
import {
  TableHead,
  Table,
  TableRow,
  TableCell,
  TableBody,
  makeStyles,
  List,
  ListItem,
  Box,
  TableContainer,
} from "@material-ui/core";

import RightDrawerModal from "../../RightDrawerModal/index";
import SliderCard from "../../SliderCard";
import { ScheduleRouteButtons } from "./components/ScheduleRouteButtons";
import {
  BEFORE_SCHEDULE,
  AFTER_SCHEDULE,
  WORKING,
} from "../../../constants/strings";
import palette from "../../styles/palette";
import SpinnerWithText from "../../SpinnerWithText/index";
import RetryErrorComponent from "./components/RetryErrorComponent";
import { useOverlay } from "../../Overlay";

const useStyles = makeStyles(() => ({
  exceptionItem: {
    padding: "0",
  },
  list: {
    textAlign: "right",
  },
  wrapper: {
    display: "flex",
    justifyContent: "space-between",
  },
  tableContainer: {
    width: "48%",
    borderWidth: "1px",
    borderColor: palette.typography.black.neutral,
    borderStyle: "solid",
    borderRadius: "5px",
  },
  diff: {
    fontSize: "0.75rem",
    fontWeight: "normal",
    letterSpacing: "-0.02em",
    marginLeft: "0.625rem",
  },
}));

const StyledTableHeader = withStyles(() => ({
  root: {
    color: palette.typography.black.medium,
    border: "none",
    textTransform: "capitalize",
    padding: "0",
    fontSize: "0.875rem",
    paddingBottom: "0.625rem",
    paddingTop: "0.9375rem",
    lineHeight: "1.25rem",
  },
}))(TableCell);

const StyledTableCell = withStyles(() => ({
  root: {
    border: "none",
    padding: "0",
    paddingBottom: "0.9375rem",
    paddingRight: "0.9375rem",
    verticalAlign: "unset",
    fontWeight: "normal",
    letterSpacing: "-0.02em",
    color: palette.typography.black.solid,
    fontSize: "1rem",
  },
}))(TableCell);

const StyledNameTableCell = withStyles(() => ({
  root: {
    color: palette.typography.black.solid,
    fontSize: "1rem",
    fontWeight: "500",
    letterSpacing: "-0.02em",
    textTransform: "capitalize",
    verticalAlign: "unset",
    border: "none",
    padding: "0",
    paddingBottom: "0.9375rem",
    paddingLeft: "0.9375rem",
    width: "40%",
  },
}))(TableCell);

const StyledListItem = withStyles(() => ({
  root: {
    padding: "0",
    fontSize: "0.75rem",
    fontWeight: "normal",
    color: palette.typography.black.medium,
    textAlign: "right",
    justifyContent: "flex-end",
  },
}))(ListItem);

const ExceptionList = ({ value, exceptions }) => {
  const classes = useStyles();
  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between">
      {value}
      <List disablePadding className={classes.list}>
        {Object.keys(exceptions).map((key) => (
          <StyledListItem
            key={key}
          >{`${key}: ${exceptions[key]}`}</StyledListItem>
        ))}
      </List>
    </Box>
  );
};

ExceptionList.propTypes = {
  value: PropTypes.number.isRequired,
  exceptions: PropTypes.object.isRequired,
};

const ExceptionRow = ({ item }) => {
  const { before, after, beforeExceptions, afterExceptions } = item;
  const classes = useStyles();
  return (
    <TableRow>
      <StyledNameTableCell className={classes.name}>
        {item.name}
      </StyledNameTableCell>
      <StyledTableCell>
        <ExceptionList value={before} exceptions={beforeExceptions} />
      </StyledTableCell>
      <StyledTableCell>
        <ExceptionList value={after} exceptions={afterExceptions} />
      </StyledTableCell>
    </TableRow>
  );
};

ExceptionRow.propTypes = {
  item: PropTypes.object.isRequired,
};

const ScheduleTable = ({ data }) => {
  const classes = useStyles();
  return (
    <TableContainer className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <StyledTableHeader />
            <StyledTableHeader>{BEFORE_SCHEDULE}</StyledTableHeader>
            <StyledTableHeader>{AFTER_SCHEDULE}</StyledTableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => {
            if (item.name === "exceptions") {
              return <ExceptionRow item={item} key={item.name} />;
            }
            return (
              <TableRow key={item.name}>
                <StyledNameTableCell className={classes.name}>
                  {item.name}
                </StyledNameTableCell>
                <StyledTableCell>{item.before}</StyledTableCell>
                <StyledTableCell>
                  {item.after}
                  <Box component="span" className={classes.diff}>
                    {item.diff}
                  </Box>
                </StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

ScheduleTable.propTypes = {
  data: PropTypes.array.isRequired,
};

const ScheduleRoute = ({
  drawerTitle,
  cardTitle,
  open,
  onSaveClick,
  onSaveAndExitClick,
  onResetClick,
  onMoreChangesClick,
  onClose,
  onRetryClick,
  showLoader,
  error,
  isRetryEnabled,
  leftData,
  rightData,
  isMakeMoreChanges,
  loadId,
  isSaveEnabled,
}) => {
  const classes = useStyles();
  const { showOverlay, hideOverlay } = useOverlay();

  useEffect(() => {
    showLoader ? showOverlay() : hideOverlay();
    return () => hideOverlay();
  }, [showLoader, showOverlay, hideOverlay]);

  return (
    <RightDrawerModal
      onClose={onClose}
      title={drawerTitle}
      open={open}
      loadId={loadId}
      actions={
        <ScheduleRouteButtons
          onSaveClick={onSaveClick}
          onSaveAndExitClick={onSaveAndExitClick}
          onResetClick={onResetClick}
          onMoreChangesClick={onMoreChangesClick}
          disabled={showLoader || (!!error && !isSaveEnabled)}
          forceEnableResetAllChanges={error}
          isMakeMoreChanges={isMakeMoreChanges}
        />
      }
    >
      <SliderCard title={cardTitle}>
        {showLoader ? (
          <SpinnerWithText text={WORKING} />
        ) : error ? (
          <RetryErrorComponent
            isRetryEnabled={isRetryEnabled}
            errorText={error}
            onRetryClick={onRetryClick}
          />
        ) : (
          <Box className={classes.wrapper}>
            <ScheduleTable data={leftData} />
            <ScheduleTable data={rightData} />
          </Box>
        )}
      </SliderCard>
    </RightDrawerModal>
  );
};

export default ScheduleRoute;

ScheduleRoute.propTypes = {
  drawerTitle: PropTypes.string,
  cardTitle: PropTypes.string,
  open: PropTypes.bool.isRequired,
  leftData: PropTypes.array.isRequired,
  rightData: PropTypes.array.isRequired,
  onSaveClick: PropTypes.func.isRequired,
  onSaveAndExitClick: PropTypes.func.isRequired,
  onResetClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onMoreChangesClick: PropTypes.func.isRequired,
  onRetryClick: PropTypes.func.isRequired,
  isSaveEnabled: PropTypes.bool.isRequired,
};
